import { useEffect, useRef } from 'react';
import { useLocation } from '@remix-run/react';

/**
 * Scrolls to the product grid after a promotional carousel card click.
 *
 * The promotional carousel sets a `scrollToProductSection` flag in
 * sessionStorage when a card is clicked. This hook watches for URL
 * search param changes and, when the flag is present, smooth-scrolls
 * to the ref element.
 *
 * Usage:
 *   const scrollRef = useScrollToProducts();
 *   <section ref={scrollRef} id="shop-products-page"> ... </section>
 */
export function useScrollToProducts() {
  const scrollTargetRef = useRef<HTMLDivElement>(null);
  const lastScrollTimestampRef = useRef<string | null>(null);
  const scrollTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const location = useLocation();

  useEffect(() => {
    let flag: string | null;
    try {
      flag = sessionStorage.getItem('scrollToProductSection');
    } catch {
      return;
    }
    if (!flag) return;

    // Only act on recent flags (within 8 seconds) to avoid stale scrolls
    const flagAge = Date.now() - parseInt(flag, 10);
    if (flagAge > 8000) {
      sessionStorage.removeItem('scrollToProductSection');
      return;
    }

    // Already handled this exact click — skip
    if (lastScrollTimestampRef.current === flag) return;

    // New card click — cancel any in-flight scroll from a previous click
    scrollTimersRef.current.forEach(clearTimeout);
    scrollTimersRef.current = [];
    lastScrollTimestampRef.current = flag;

    const handledFlag = flag;

    const doScroll = () => {
      scrollTargetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Scroll multiple times to survive competing scroll resets from third-party scripts
    scrollTimersRef.current = [
      setTimeout(doScroll, 150),
      setTimeout(doScroll, 600),
      setTimeout(doScroll, 1200),
      setTimeout(() => {
        try {
          const currentFlag = sessionStorage.getItem('scrollToProductSection');
          if (currentFlag === handledFlag) {
            sessionStorage.removeItem('scrollToProductSection');
          }
        } catch { /* ignore */ }
        scrollTimersRef.current = [];
      }, 3000),
    ];

    return () => {
      scrollTimersRef.current.forEach(clearTimeout);
      scrollTimersRef.current = [];
    };
  }, [location.search, location.key]);

  return scrollTargetRef;
}
